'use client'

import {useTranslations} from 'next-intl'
import {useState} from 'react'
import {Button, Icon, Select} from '@/design-system'
import {api, ApiError} from '@/lib/api/client'
import type {SavedKind, SavedState, SavedStatus} from '@/lib/api/types'
import {useToast} from '../Toast'
import './saved.css'

const STATUSES: SavedStatus[] = ['saved', 'applied', 'dismissed']

/**
 * Save a catalogue row to the shortlist, and — on a detail page — say where
 * you are with it (CAB-10).
 *
 * Optimistic: the control changes the moment it is pressed, and goes back
 * with a toast if the request fails. A student saving five cards in a row
 * should not wait on five round trips to see five bookmarks.
 *
 * The initial state comes from the page (`saved_status` on every summary),
 * so nothing is fetched to render this.
 */
export function SaveButton({
  kind,
  slug,
  name,
  status: initial,
  withStatus = false,
  onChange
}: {
  kind: SavedKind
  slug: string
  /** For the accessible label: "Save Chevening to your shortlist". */
  name: string
  status: SavedStatus | null | undefined
  /** On a detail page: also offer the status select once saved. */
  withStatus?: boolean
  /** After a save or removal went through — e.g. to re-render a page that shows more once saved. */
  onChange?: () => void
}) {
  const t = useTranslations('saved')
  const tErrors = useTranslations('errors')
  const toast = useToast()
  const [status, setStatus] = useState<SavedStatus | null>(initial ?? null)
  const [pending, setPending] = useState(false)
  const path = `/me/saved/${kind}/${encodeURIComponent(slug)}`

  async function change(next: SavedStatus | null) {
    const previous = status
    setStatus(next)
    setPending(true)
    try {
      if (next === null) {
        await api.delete(path)
      } else {
        const saved = await api.put<SavedState>(path, {status: next})
        setStatus(saved.status)
      }
      onChange?.()
    } catch (error) {
      setStatus(previous)
      toast.show(tErrors(error instanceof ApiError ? error.messageKey : 'internal'), 'error')
    } finally {
      setPending(false)
    }
  }

  const isSaved = status !== null

  return (
    <div className="u-save" data-testid={`save-${kind}-${slug}`}>
      <Button
        type="button"
        variant={isSaved ? 'secondary' : 'ghost'}
        size="sm"
        aria-pressed={isSaved}
        aria-label={isSaved ? t('unsaveLabel', {name}) : t('saveLabel', {name})}
        disabled={pending}
        onClick={() => change(isSaved ? null : 'saved')}
      >
        <Icon name={isSaved ? 'bookmark-check' : 'bookmark'} size={16} />
        <span>{isSaved ? t('saved') : t('save')}</span>
      </Button>

      {withStatus && isSaved ? (
        <div className="u-save__status">
          <Select
            label={t('statusLabel')}
            value={status}
            disabled={pending}
            options={STATUSES.map((value) => ({value, label: t(`status_${value}`)}))}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
              change(event.target.value as SavedStatus)
            }
          />
        </div>
      ) : null}
    </div>
  )
}
